import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 transition-smooth",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        hero: "bg-gradient-hero text-primary-foreground shadow-md hover:shadow-lg hover:scale-[1.02] transform transition-all duration-300",
        cta: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-soft hover:shadow-hover border-2 border-primary/20",
        minimal: "bg-transparent text-foreground hover:bg-muted border border-border/50 hover:border-primary/30",
        // SCALE AI Brandbook specific variants
        "brand-primary": "bg-primary text-primary-foreground hover:bg-primary/90 shadow-soft hover:shadow-hover transform hover:scale-[1.02] transition-all duration-300",
        "brand-secondary": "bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-soft hover:shadow-hover",
        "brand-action": "bg-action-blue text-action-blue-foreground hover:bg-action-blue/90 shadow-soft hover:shadow-hover",
        "brand-success": "bg-success text-success-foreground hover:bg-success/90 shadow-soft hover:shadow-hover",
        "brand-outline": "border-2 border-primary bg-background text-primary hover:bg-primary hover:text-primary-foreground shadow-soft hover:shadow-hover",
        "brand-ghost": "text-secondary hover:bg-accent/20 hover:text-secondary",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-12 rounded-lg px-8 text-base",
        xl: "h-14 rounded-xl px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
